import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setApiAccessToken, syncApiAccessTokenFromStorage } from "./api";
import {
  clearPrivateOfflineStorage,
  readOfflineUser,
  readStoredAccessToken,
  writeOfflineUser,
  writeStoredAccessToken,
  writeStoredRefreshToken,
} from "./authStorage";
import { clearPrivateApiCaches, unsubscribeFromWebPush } from "./pwa";
import type { User } from "./types";

interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, inviteCode: string) => Promise<void>;
  clearSession: (options?: { notifyPushServer?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isUnauthorizedError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "statusCode" in error
    && (error as { statusCode?: number }).statusCode === 401;
}

type InvalidatedSessionCleanup = {
  clearPersistedState: () => void;
  clearRuntimeState: () => void;
  clearQueries: () => void;
  clearCaches: () => Promise<void>;
};

export async function clearInvalidatedSession({
  clearPersistedState,
  clearRuntimeState,
  clearQueries,
  clearCaches,
}: InvalidatedSessionCleanup) {
  clearPersistedState();
  clearRuntimeState();
  clearQueries();
  await clearCaches().catch(() => undefined);
}

function readInitialAuthState() {
  const storedAccessToken = readStoredAccessToken();
  if (storedAccessToken) {
    setApiAccessToken(storedAccessToken);
  }
  return {
    user: readOfflineUser(),
    accessToken: storedAccessToken,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [initialAuth] = useState(() => readInitialAuthState());
  const [user, setUser] = useState<User | null>(initialAuth.user);
  const [accessToken, setAccessToken] = useState<string | null>(initialAuth.accessToken);
  const [isLoading, setIsLoading] = useState(() => !(initialAuth.user || initialAuth.accessToken));

  const refreshUser = useCallback(async () => {
    syncApiAccessTokenFromStorage();
    const currentUser = await api.me();
    setUser(currentUser);
    writeOfflineUser(currentUser);
    return currentUser;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const storedAccessToken = readStoredAccessToken();
    if (storedAccessToken) {
      setAccessToken(storedAccessToken);
      setApiAccessToken(storedAccessToken);
    }

    refreshUser()
      .catch(async (error) => {
        if (cancelled) return;
        if (isUnauthorizedError(error)) {
          await clearInvalidatedSession({
            clearPersistedState: clearPrivateOfflineStorage,
            clearRuntimeState: () => {
              setApiAccessToken(null);
              setAccessToken(null);
              setUser(null);
              setIsLoading(false);
            },
            clearQueries: () => queryClient.clear(),
            clearCaches: clearPrivateApiCaches,
          });
          return;
        }
        const offlineUser = readOfflineUser();
        if (offlineUser) {
          setUser(offlineUser);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.login(email.trim().toLowerCase(), password);
    clearPrivateOfflineStorage();
    await clearPrivateApiCaches().catch(() => undefined);
    queryClient.clear();
    setUser(response.user);
    setAccessToken(response.accessToken || null);
    setApiAccessToken(response.accessToken || null);
    writeStoredAccessToken(response.accessToken || null);
    writeStoredRefreshToken(response.refreshToken || null);
    writeOfflineUser(response.user);
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const signup = useCallback(async (email: string, password: string, inviteCode: string) => {
    const response = await api.signup(email.trim().toLowerCase(), password, inviteCode.trim());
    clearPrivateOfflineStorage();
    await clearPrivateApiCaches().catch(() => undefined);
    queryClient.clear();
    setUser(response.user);
    setAccessToken(response.accessToken || null);
    setApiAccessToken(response.accessToken || null);
    writeStoredAccessToken(response.accessToken || null);
    writeStoredRefreshToken(response.refreshToken || null);
    writeOfflineUser(response.user);
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const clearSession = useCallback(async ({ notifyPushServer = true }: { notifyPushServer?: boolean } = {}) => {
    await unsubscribeFromWebPush({ notifyServer: notifyPushServer }).catch(() => undefined);
    setUser(null);
    setAccessToken(null);
    setApiAccessToken(null);
    writeStoredAccessToken(null);
    writeStoredRefreshToken(null);
    clearPrivateOfflineStorage();
    queryClient.clear();
    await clearPrivateApiCaches().catch(() => undefined);
  }, [queryClient]);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    await clearSession();
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.update()));
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    accessToken,
    isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    clearSession,
    logout,
    refreshUser,
  }), [accessToken, clearSession, isLoading, login, logout, refreshUser, signup, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
